import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  BookOpen,
  Edit2,
  Eye,
  ImageIcon,
  Plus,
  Save,
  Sparkles,
  Upload,
  X,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { adminApi } from "../utils/admin-api-axios";

const emptyForm = {
  sourceName: "",
  sourceUrl: "",
  title: "",
  blurb: "",
  excerpt: "",
  imageUrl: "",
};

const ManageResources = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resources, setResources] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("drafts");

  const fileInputRef = useRef(null);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const fetchResources = async () => {
    try {
      const data = await adminApi.getAllResources();
      if (data.success) {
        setResources(data.resources);
      }
    } catch (error) {
      console.error("Failed to fetch resources:", error);
      showMessage("error", error.message);
    }
  };

  useEffect(() => {
    fetchResources();
  }, []);

  // Handle ?edit=:id from the Preview page — pre-fill the form for editing
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && resources.length > 0) {
      const target = resources.find((r) => r._id === editId);
      if (target) {
        setForm({
          sourceName: target.sourceName || "",
          sourceUrl: target.sourceUrl || "",
          title: target.title || "",
          blurb: target.blurb || "",
          excerpt: target.excerpt || "",
          imageUrl: target.imageUrl || "",
        });
        setEditingId(target._id);
        window.scrollTo({ top: 0, behavior: "smooth" });
        searchParams.delete("edit");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [resources, searchParams, setSearchParams]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleFetchPreview = async () => {
    if (!form.sourceUrl) {
      showMessage("error", "Enter a source URL first");
      return;
    }

    setFetchingPreview(true);
    try {
      const data = await adminApi.fetchResourcePreview(form.sourceUrl);
      if (data.success && data.preview?.imageUrl) {
        setForm((prev) => ({
          ...prev,
          imageUrl: data.preview.imageUrl,
          title: prev.title || data.preview.title || "",
        }));
        showMessage("success", "Image fetched successfully");
      } else {
        showMessage(
          "error",
          data.message || "Couldn't fetch image. Upload one manually."
        );
      }
    } catch (error) {
      showMessage("error", error.message || "Couldn't fetch image");
    } finally {
      setFetchingPreview(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showMessage("error", "Please select an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showMessage("error", "Image must be smaller than 10MB");
      return;
    }

    setUploading(true);
    try {
      const data = await adminApi.uploadResourceImage(file);
      if (data.success && data.imageUrl) {
        setForm((prev) => ({ ...prev, imageUrl: data.imageUrl }));
        showMessage("success", "Image uploaded successfully");
      } else {
        showMessage("error", data.message || "Upload failed");
      }
    } catch (error) {
      showMessage("error", error.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = editingId
        ? await adminApi.updateResource(editingId, form)
        : await adminApi.createResource(form);

      if (data.success) {
        showMessage(
          "success",
          editingId
            ? "Resource updated successfully"
            : "Resource saved as draft. Preview it to publish."
        );
        resetForm();
        fetchResources();
        // If we just created a new draft, switch to the drafts tab
        if (!editingId) setActiveTab("drafts");
      } else {
        showMessage("error", data.message || "Something went wrong");
      }
    } catch (error) {
      showMessage("error", error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background";

  const drafts = resources.filter((r) => r.status !== "published");
  const published = resources.filter((r) => r.status === "published");

  const ResourceCard = ({ resource }) => (
    <button
      type="button"
      onClick={() => navigate(`/admin/resources/${resource._id}`)}
      className="text-left bg-white border border-border rounded-lg overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col group"
    >
      <div className="w-full aspect-[16/10] bg-muted overflow-hidden flex items-center justify-center">
        {resource.imageUrl ? (
          <img
            src={resource.imageUrl}
            alt={resource.sourceName}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        ) : (
          <ImageIcon className="w-10 h-10 text-gray-300" />
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="text-xs font-semibold uppercase tracking-wide text-primary mb-1 truncate">
          {resource.sourceName}
        </div>
        {resource.title && (
          <div className="font-semibold text-gray-800 text-sm mb-1 line-clamp-1">
            {resource.title}
          </div>
        )}
        <p className="text-xs text-gray-600 line-clamp-2 mb-3">
          {resource.blurb}
        </p>
        <div className="mt-auto flex items-center justify-between">
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${resource.status === "published"
                ? "bg-primary/10 text-primary"
                : "bg-gray-100 text-gray-600"
              }`}
          >
            {resource.status === "published" ? "Published" : "Draft"}
          </span>
          <span className="text-xs text-gray-400 inline-flex items-center group-hover:text-primary transition-colors">
            <Eye className="w-3 h-3 mr-1" />
            Preview
          </span>
        </div>
      </div>
    </button>
  );

  const currentList = activeTab === "drafts" ? drafts : published;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">Manage Resources</h2>
        <div className="flex items-center text-sm text-gray-500">
          <BookOpen className="w-4 h-4 mr-2" />
          {resources.length} {resources.length === 1 ? "resource" : "resources"}
        </div>
      </div>

      {message && (
        <div
          className={`px-4 py-3 rounded-md text-sm ${message.type === "success"
              ? "bg-primary/10 text-primary border border-primary/20"
              : "bg-red-50 text-red-800 border border-red-200"
            }`}
        >
          {message.text}
        </div>
      )}

      {/* Form Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            {editingId ? (
              <>
                <Edit2 className="w-5 h-5 mr-2 text-primary" />
                Edit Resource
              </>
            ) : (
              <>
                <Plus className="w-5 h-5 mr-2 text-primary" />
                Add New Resource
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source Name <span className="text-red-500">*</span>
                </label>
                <input
                  name="sourceName"
                  value={form.sourceName}
                  onChange={handleChange}
                  placeholder="e.g. dogoodthings_farm"
                  required
                  className={inputClass}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source URL <span className="text-red-500">*</span>
                </label>
                <input
                  name="sourceUrl"
                  value={form.sourceUrl}
                  onChange={handleChange}
                  placeholder="https://..."
                  type="url"
                  required
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-gray-400">(optional)</span>
              </label>
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g. We can't do it alone"
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Blurb <span className="text-red-500">*</span>
                <span className="font-normal text-gray-500 ml-2">
                  — 1-2 sentences on why this is here
                </span>
              </label>
              <textarea
                name="blurb"
                value={form.blurb}
                onChange={handleChange}
                rows="3"
                required
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Excerpt / Quote{" "}
                <span className="text-gray-400">(optional)</span>
                <span className="font-normal text-gray-500 ml-2">
                  — a passage pulled from the source
                </span>
              </label>
              <textarea
                name="excerpt"
                value={form.excerpt}
                onChange={handleChange}
                rows="6"
                className={inputClass}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <label className="block text-sm font-medium text-gray-700">
                  Image <span className="text-gray-400">(optional)</span>
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleFetchPreview}
                    disabled={fetchingPreview || !form.sourceUrl || uploading}
                    className="flex items-center text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1" />
                    {fetchingPreview ? "Fetching..." : "Fetch from source"}
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={uploading || fetchingPreview}
                    className="flex items-center text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" />
                    {uploading ? "Uploading..." : "Upload image"}
                  </button>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <input
                name="imageUrl"
                value={form.imageUrl}
                onChange={handleChange}
                placeholder="https://... or use the buttons above"
                type="url"
                className={inputClass}
              />
              {form.imageUrl && (
                <div className="mt-2 rounded-md overflow-hidden border border-input max-w-xs">
                  <img
                    src={form.imageUrl}
                    alt="Preview"
                    className="w-full h-auto object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1.5">
                Tip: For Instagram, take a screenshot (
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[10px]">
                  Win + Shift + S
                </kbd>{" "}
                or{" "}
                <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[10px]">
                  Cmd + Shift + 4
                </kbd>
                ) and use "Upload image".
              </p>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={loading || uploading || fetchingPreview}
                className="flex items-center px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading
                  ? "Saving..."
                  : editingId
                    ? "Update Resource"
                    : "Save as Draft"}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex items-center px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md transition-colors"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Tabbed Drafts / Published */}
      <Card>
        <CardHeader className="pb-0">
          <div className="flex items-center border-b border-border -mb-px">
            <button
              type="button"
              onClick={() => setActiveTab("drafts")}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "drafts"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              Drafts
              <span
                className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${activeTab === "drafts"
                    ? "bg-primary/10 text-primary"
                    : "bg-gray-100 text-gray-600"
                  }`}
              >
                {drafts.length}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("published")}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === "published"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              Published
              <span
                className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${activeTab === "published"
                    ? "bg-primary/10 text-primary"
                    : "bg-gray-100 text-gray-600"
                  }`}
              >
                {published.length}
              </span>
            </button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {currentList.length === 0 ? (
            <p className="text-gray-500 text-sm py-6 text-center">
              {activeTab === "drafts"
                ? "No drafts. New resources start here."
                : "Nothing published yet. Open a draft to preview and publish it."}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {currentList.map((r) => (
                <ResourceCard key={r._id} resource={r} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManageResources;